import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getOnboardingByCustomer } from '../../api/onboarding';

/**
 * Landing page after Stripe checkout
 * Fetches the onboarding session by customer ID and redirects to the wizard
 */
export function OnboardingStartPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const customerId = searchParams.get('customer_id');

  useEffect(() => {
    if (!customerId) {
      setError('Missing customer information. Please contact support.');
      return;
    }

    const fetchSession = async () => {
      try {
        const data = await getOnboardingByCustomer(customerId);
        // Redirect to the actual wizard
        navigate(`/onboarding/${data.sessionId}`, { replace: true });
      } catch (err) {
        // The webhook might not have processed yet, retry a few times
        if (retryCount < 5) {
          setTimeout(() => {
            setRetryCount((c) => c + 1);
          }, 2000); // Wait 2 seconds before retry
        } else {
          setError(
            'Unable to load your onboarding session. This can happen if your payment is still processing. Please try refreshing the page in a few moments.'
          );
        }
      }
    };

    fetchSession();
  }, [customerId, navigate, retryCount]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">!</span>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Something went wrong
          </h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition"
          >
            Try Again
          </button>
          <p className="mt-4 text-sm text-gray-500">
            Need help?{' '}
            <a
              href="mailto:support@niftee.ai"
              className="text-primary-500 hover:underline"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 flex items-center justify-center p-4">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-primary-500 animate-spin mx-auto mb-4" />
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Setting up your onboarding...
        </h1>
        <p className="text-gray-600">
          {retryCount > 0
            ? `Processing your payment... (${retryCount}/5)`
            : 'Just a moment...'}
        </p>
      </div>
    </div>
  );
}
